/**
 * SanPlumb — Hunter (Fase 3c)
 *
 * Pantalla de cálculo: contador editable de aparatos (AF/AC por separado) y
 * panel de resultado en vivo. Todo el cálculo viene del engine real:
 *   UG → interpolateHunter() → Q → selectDiameterByVelocity() → Ø + velocidad
 *
 * Portado de `screens.jsx` (Hunter) del diseño, reemplazando la fórmula
 * aproximada del diseño por el engine normativo.
 */

import type { CountsState, UsoMode } from '../../types/ui';
import { APARATOS_UI, ugFor, calcUG } from '../../constants/aparatosUI';
import { interpolateHunter, getVelocity, selectDiameterByVelocity } from '../../engine/hunter';
import { VELOCIDADES_MAX } from '../../constants/is010';
import { TIPOLOGIAS, tipologiaByCode, type TipologiaCode } from '../../constants/tipologias';
import { iconForAparato, Icon } from '../Icon';

export interface HunterProps {
  counts: CountsState;
  setCounts: (next: CountsState) => void;
  mode: UsoMode;
  /** Tipología activa (auto-detectada o override). */
  tipologia: TipologiaCode;
  /** Override del usuario; null = auto-detección. */
  tipologiaOverride: TipologiaCode | null;
  onSetTipologia: (t: TipologiaCode | null) => void;
  onOpenIngesta: () => void;
}

export function Hunter({
  counts,
  setCounts,
  mode,
  tipologia,
  tipologiaOverride,
  onSetTipologia,
  onOpenIngesta,
}: HunterProps): JSX.Element {
  const { af, ac, ug } = calcUG(counts, mode);
  const q = interpolateHunter(ug);
  const sel = selectDiameterByVelocity(q);
  const v = getVelocity(q, sel.mm);
  const vMax = VELOCIDADES_MAX[sel.mm] ?? 2.2;
  const ok = v <= vMax;

  const predomLabel =
    af >= ac ? 'AF · agua fría predominante' : 'AC · agua caliente predominante';

  function setCount(id: string, value: number): void {
    setCounts({ ...counts, [id]: Math.max(0, value) });
  }

  return (
    <div className="hunter screen-anim" data-screen-label="01 Cálculo Hunter">
      <div className="hunter-form">
        <div className="eyebrow">PASO 01 · CÁLCULO HUNTER · IS.010</div>
        <div className="h-title" style={{ marginTop: 8 }}>
          Aparatos sanitarios
        </div>
        <div className="h-sub">
          Cantidad de aparatos por sistema. El gasto probable se calcula en tiempo real para{' '}
          <b style={{ color: 'var(--af)' }}>AF</b> y <b style={{ color: 'var(--ac)' }}>AC</b> por
          separado.
        </div>

        <TipologiaSelector
          active={tipologia}
          override={tipologiaOverride}
          onChange={onSetTipologia}
        />

        {APARATOS_UI.map((ap) => {
          const c = counts[ap.id] ?? 0;
          const { ugAF, ugAC } = ugFor(ap.id, mode);
          const totAF = +(c * ugAF).toFixed(2);
          const totAC = +(c * ugAC).toFixed(2);
          return (
            <div className="app-row" key={ap.id}>
              <span className={`name ${c === 0 ? 'dim' : ''}`}>
                <span className="ap-row-ico">{iconForAparato(ap.id)}</span>
                <span className="ap-row-name-text">
                  {ap.name}
                  {c > 0 && (
                    <span className="ap-most">
                      {ap.mostUsed === 'AC' ? '+ AC' : ugAC > 0 ? '+ AF + AC' : 'AF only'}
                    </span>
                  )}
                </span>
              </span>
              <div className="counter">
                <button onClick={() => setCount(ap.id, c - 1)}>−</button>
                <span className={`val ${c === 0 ? 'zero' : ''}`}>{c}</span>
                <button onClick={() => setCount(ap.id, c + 1)}>+</button>
              </div>
              <span className="ug-split">
                {c === 0 ? (
                  <span className="ug zero">— UG</span>
                ) : (
                  <>
                    <span className="ug af">
                      {totAF} <span className="ug-tag">AF</span>
                    </span>
                    {ugAC > 0 && (
                      <span className="ug ac">
                        {totAC} <span className="ug-tag">AC</span>
                      </span>
                    )}
                  </>
                )}
              </span>
            </div>
          );
        })}

        <div className="hunter-actions">
          <button className="btn primary" onClick={onOpenIngesta}>
            Continuar → Paso 2 {Icon.arrow}
          </button>
        </div>
      </div>

      <div className="hunter-result">
        <div className="result">
          <div className="result-h">▸ Resultado · Uso {mode === 'pub' ? 'público' : 'residencial'}</div>

          <div className="ug-grid">
            <div className="ug-cell total">
              <div className="ug-cell-k">UG · AF (red fría)</div>
              <div className="ug-cell-v big">{af}</div>
              <div className="ug-cell-foot">sizing alimentación</div>
            </div>
            <div className="ug-cell">
              <div className="ug-cell-k ac-k">UG · AC (red caliente)</div>
              <div className="ug-cell-v ac-v">{ac}</div>
              <div className="ug-cell-foot">sizing rama caliente</div>
            </div>
          </div>

          <div className="predom-note">
            <span className="dot" style={{ background: af >= ac ? 'var(--af)' : 'var(--ac)' }} />
            <b>Predomina:</b> {predomLabel}
          </div>

          <div className="kv">
            <span className="k">Q probable (tanque)</span>
            <span className="v big">{q} L/s</span>
          </div>
          <div className="kv">
            <span className="k">Diámetro alimentación</span>
            <span className="v">
              {sel.d} ({sel.mm}mm)
            </span>
          </div>
          <div className="kv">
            <span className="k">Velocidad en matriz</span>
            <span className={`v ${ok ? 'ok' : 'warn'}`}>
              {v} m/s {ok ? '✓' : '⚠'}
            </span>
          </div>
          <div className="kv">
            <span className="k">V máx. §2.3(f)</span>
            <span className="v">{vMax} m/s</span>
          </div>
          <div className="kv">
            <span className="k">Presión mín. salida</span>
            <span className="v">≥ 2 m.c.a.</span>
          </div>
        </div>

        <div className="section-h">Velocidades máximas IS.010 §2.3(f)</div>
        <div className="vel-table">
          <div className="vt-h">
            <span>Diámetro</span>
            <span>V máx.</span>
          </div>
          {Object.entries(VELOCIDADES_MAX).map(([mm, vmx]) => {
            const n = Number(mm);
            const lbl =
              n === 15 ? '½" (15mm)' : n === 20 ? '¾" (20mm)' : n === 25 ? '1" (25mm)' : `1¼" (${mm}mm)`;
            const cur = n === sel.mm;
            return (
              <div className={`vt-r ${cur ? 'cur' : ''}`} key={mm}>
                <span>{lbl}</span>
                <span>{vmx} m/s</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// TipologiaSelector — selector §XI del tipo de baño
// ──────────────────────────────────────────────────────────────────

interface TipologiaSelectorProps {
  active: TipologiaCode;
  override: TipologiaCode | null;
  onChange: (next: TipologiaCode | null) => void;
}

function TipologiaSelector({
  active,
  override,
  onChange,
}: TipologiaSelectorProps): JSX.Element {
  const spec = tipologiaByCode(active);
  const isAuto = override === null;

  return (
    <div className="tipologia-box">
      <div className="tipologia-head">
        <div>
          <div className="eyebrow">TIPOLOGÍA DEL BAÑO</div>
          <div className="tipologia-title">
            {spec.label}
            {isAuto && <span className="tipologia-auto">auto</span>}
          </div>
          <div className="tipologia-desc">
            {spec.desc} · {spec.room.w}m × {spec.room.d}m × {spec.room.h}m
          </div>
        </div>
        {!isAuto && (
          <button
            className="btn ghost"
            style={{ padding: '6px 10px', fontSize: 10 }}
            onClick={() => onChange(null)}
            title="Volver a detección automática"
          >
            ↻ auto
          </button>
        )}
      </div>
      <div className="tipologia-grid">
        {TIPOLOGIAS.map((t) => {
          const cur = t.code === active;
          return (
            <button
              key={t.code}
              className={`tipologia-chip ${cur ? 'cur' : ''}`}
              onClick={() => onChange(t.code)}
              title={t.desc}
            >
              <span className="tipologia-chip-code">{t.code}</span>
              <span className="tipologia-chip-label">{t.label.split('·')[1]?.trim() ?? t.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
